// ===============================
// InterfaceAstreinteThoracique.jsx
// ===============================
// Page intermédiaire médecin pour l'astreinte thoracique
// - Accès à la saisie / modification des desiderata d'astreinte
// - Accès en lecture à la liste d'astreintes validée
// (même structure que InterfaceMedecins.jsx pour PR2)
// ===============================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext.jsx";
import LogoutButton from "./components/LogoutButton.jsx";

const cardStyle = {
  flex: 1,
  minWidth: "260px",
  maxWidth: "420px",
  padding: "24px",
  borderRadius: "16px",
  background: "#ffffff",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  border: "1px solid #e5e7eb",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const titleStyle = {
  fontSize: "18px",
  fontWeight: 700,
  margin: 0,
  color: "#111827",
};

const textStyle = {
  fontSize: "14px",
  color: "#4b5563",
  lineHeight: 1.5,
};

const btnStyle = {
  marginTop: "12px",
  padding: "12px 18px",
  borderRadius: "9999px",
  border: "1px solid #111827",
  background: "#111827",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
  alignSelf: "flex-start",
};

export default function InterfaceAstreinteThoracique() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Vérification de participation
  const [isParticipating, setIsParticipating] = useState(null);
  const [showParticipationModal, setShowParticipationModal] = useState(false);

  useEffect(() => {
    const checkParticipation = async () => {
      if (!user?.nom || !user?.prenom) return;

      try {
        const res = await fetch("/api/v2/medecins-astreintes-thoracique");
        if (res.ok) {
          const medecins = await res.json();

          const normalize = (str) =>
            String(str || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

          const userNomNorm = normalize(user.nom);
          const userPrenomNorm = normalize(user.prenom);

          const isInList = medecins.some((m) => {
            return normalize(m.nom) === userNomNorm && normalize(m.prenom) === userPrenomNorm;
          });

          setIsParticipating(isInList);
          if (!isInList) setShowParticipationModal(true);
          else setShowParticipationModal(false);
        } else {
          setIsParticipating(null);
          setShowParticipationModal(false);
        }
      } catch (err) {
        console.error("❌ Erreur vérification participation astreinte thoracique:", err);
        setIsParticipating(null);
        setShowParticipationModal(false);
      }
    };

    checkParticipation();
  }, [user]);

  const handleOpenDesiderata = () => {
    navigate("/desiderata-astreinte-thoracique");
  };

  const handleOpenListeAstreintes = () => {
    navigate("/liste-garde-validee?type=ASTREINTE_THORACIQUE");
  };

  const handleBackToPortail = () => {
    navigate("/portail-medecin");
  };

  // Modal de participation requise
  if (showParticipationModal && isParticipating === false && user?.nom && user?.prenom) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
          padding: "24px",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <h2 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "16px", color: "#111827" }}>
              Participation requise
            </h2>
            <p style={{ fontSize: "16px", lineHeight: 1.6, color: "#374151", marginBottom: "24px" }}>
              Si vous souhaitez participer à l&apos;astreinte thoracique, merci de cocher la case
              &quot;Je participe à cette activité&quot; sur la page Espace médecins.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => { window.location.href = "/portail-medecin"; }}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retour à l&apos;Espace médecins
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f4f6",
        padding: "40px 16px",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div style={{ maxWidth: "960px", margin: "0 auto" }}>
        {/* Titre page */}
        <header style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h1 style={{ fontSize: "32px", fontWeight: 800, margin: 0, color: "#111827" }}>
                Interface médecin – Astreinte thoracique
              </h1>
              <p style={{ marginTop: "8px", fontSize: "15px", color: "#4b5563", maxWidth: "640px" }}>
                Depuis cette page, vous pouvez renseigner vos desiderata d&apos;astreinte thoracique
                et consulter la dernière liste d&apos;astreintes validée par le responsable.
              </p>
            </div>
            {isAuthenticated && (
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={handleBackToPortail}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "9999px",
                    border: "1px solid #d1d5db",
                    background: "#ffffff",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  ← Retour au portail médecin
                </button>
                <LogoutButton />
              </div>
            )}
          </div>
        </header>

        {/* Cartes principales */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
          {/* Carte saisie desiderata */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>Saisir / modifier mes desiderata</h2>
            <p style={textStyle}>
              Accès à la page de saisie de vos disponibilités pour l&apos;astreinte thoracique
              (quadrimestre mai–août 2026). Tous les jours doivent être renseignés avant validation.
            </p>
            <button type="button" style={btnStyle} onClick={handleOpenDesiderata}>
              Ouvrir la saisie des desiderata
            </button>
          </section>

          {/* Carte consultation liste d'astreintes */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>Consulter la liste d&apos;astreintes</h2>
            <p style={textStyle}>
              Visualisation de la dernière liste d&apos;astreintes thoraciques validée
              par le responsable (lecture seule).
            </p>
            <button type="button" style={btnStyle} onClick={handleOpenListeAstreintes}>
              Voir la liste d&apos;astreintes
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
